import React, { useState } from 'react';

import { PersonalizationWidgetProvider } from '@vp/design-personalization-widget';
import { CustomLoggerProvider, useCustomLogger } from '@vp/pdp-ubik-logging';
import { Box, ScreenClassProvider } from '@vp/swan';
import { IdentityProvider, useSwanStyleKeys } from '@vp/ubik-context';
import { LocalizationsProvider, NestedStringObject } from '@vp/ubik-localizations-provider';

import { AuthProvider } from '../contexts/AuthProvider';

import { PersonalizationCardWrapper } from './brandkit';
import { FavoritesHeartWrapper, FavoritesHeartWrapperProps } from './favorites-heart';
import {
  HERO_GALLERY_SWAN_STYLES,
  PDPHeroCarousel,
  PDPHeroCarouselProps,
} from './pdp-hero-carousel';

export interface FragmentProps {
  pdpHeroCarouselProps: PDPHeroCarouselProps;
  favoriteHeartProps?: FavoritesHeartWrapperProps;
  locale: string;
  mpvId: string;
  localizations: NestedStringObject;
  personalizationWidgetLocalizations: NestedStringObject;
  environment?: string;
  designPersonalizationContextIdOverride?: string;
  disableBrandKit: boolean;
}

export const Fragment: React.FC<FragmentProps> = (props) => {
  // @vp/favorites-heart-v2's FavoriteHeartProvider registers its own SWAN style keys internally,
  // so only the carousel's own styles need to be registered here.
  useSwanStyleKeys(HERO_GALLERY_SWAN_STYLES);

  return (
    <IdentityProvider auth={{ culture: props.locale, developmentMode: false }}>
      <AuthProvider>
        <ScreenClassProvider>
          <CustomLoggerProvider
            context={{ mpvId: props.mpvId, locale: props.locale, environment: props.environment }}
          >
            <WrappedFragment {...props} />
          </CustomLoggerProvider>
        </ScreenClassProvider>
      </AuthProvider>
    </IdentityProvider>
  );
};

const WrappedFragment: React.FC<FragmentProps> = (props) => {
  const { logger } = useCustomLogger();
  const [hasBrandkitApiError, setHasBrandkitApiError] = useState(false);
  const hasDesignPersonalizationContextIdOverride = !!props.designPersonalizationContextIdOverride;
  const shouldShowBrandKit =
    !props.disableBrandKit &&
    (props.pdpHeroCarouselProps.showBrandKit || hasDesignPersonalizationContextIdOverride);

  return (
    <LocalizationsProvider logger={logger} localizations={props.localizations}>
      <Box>
        <Box padding={'5'} style={{ position: 'absolute', top: 0, right: 0, zIndex: 3 }}>
          {props.favoriteHeartProps && <FavoritesHeartWrapper {...props.favoriteHeartProps} />}
        </Box>
        {shouldShowBrandKit ? (
          <PersonalizationWidgetProvider
            localizations={props.personalizationWidgetLocalizations}
            localStoragePageKey="pdp"
            configuration={{
              allowRestoreFromBrand: true,
              personalizationToggleOffByDefault: true,
            }}
          >
            <PDPHeroCarousel
              {...props.pdpHeroCarouselProps}
              onBrandkitApiError={setHasBrandkitApiError}
              designPersonalizationContextIdOverride={props.designPersonalizationContextIdOverride}
              showBrandKit={shouldShowBrandKit}
            />

            <Box
              mt="4"
              paddingBottom={{
                xs: 6,
                sm: 6,
                md: 0,
                lg: 0,
              }}
            >
              <PersonalizationCardWrapper hasApiError={hasBrandkitApiError} />
            </Box>
          </PersonalizationWidgetProvider>
        ) : (
          <PDPHeroCarousel {...props.pdpHeroCarouselProps} showBrandKit={false} />
        )}
      </Box>
    </LocalizationsProvider>
  );
};
